// Tokens for the Figma section "Tournaments Elements" (2864:14973) - page "PAVEL DRAFT": the
// "My registered tournaments" panel, "Modal / Tournament Info — with Timer" (5157:12764) and
// "Modal / Tournament Rules" (5157:14681). Every number is in the 390-wide mobile design space and
// is scaled once by the component, like the rest of the redesign.
//
// Colours are the file's variables (text/accent, gold/50..300, text/secondary, text/muted,
// surface/sunken, surface/overlay, border/subtle, border/gold); the gradients are the literal
// values the design context returns.
import { EVENT_FONT, EVENT_FONT_CONDENSED } from '../Event/eventTheme';
import type { SnG } from '../Middle/TournamentsSection';

export const TOURNAMENT_FONT = EVENT_FONT;
/** The design asks for Sofia Sans Extra Condensed; the app ships Semi Condensed, the closest cut. */
export const TOURNAMENT_FONT_CONDENSED = EVENT_FONT_CONDENSED;

export const tournamentColors = {
	accent: '#FFFF99',
	gold50: '#FFF5B9',
	gold100: '#FFE9A8',
	gold200: '#FFD766',
	gold300: '#FFC500',
	secondary: '#D9C4BC',
	muted: '#A88E86',
	sunken: '#0F0301',
	overlay: '#4A1A0E',
	borderSubtle: '#5C2416',
	borderGold: '#E89200',
	/** Row / card hairline. */
	rowBorder: '#DA600E',
	/** The bright gold that rims the entry banner and the countdown capsule. */
	brightGold: '#FFF908',
	/** Button / Unregister's rim. */
	buttonRim: '#FCF17B',
} as const;

/** "Button / Unregister" and the modal's cancel action: a dark red vertical ramp. */
export const RED_BUTTON_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.8', '#320400'],
];
/**
 * The tournament sheets ("Tournaments / Mobile — Tournament Info Modal (info)", 5157:13956): the
 * file's "linear red" ramp - the same one as the cancel button - instead of the old dark tint over
 * the app backdrop, under a 1 rim that runs from pale gold at the top to amber at the bottom.
 */
export const PANEL_FILL = RED_BUTTON_FILL;
export const PANEL_RIM: Array<[string, string]> = [
	['0', '#FCF17B'],
	['1', '#E08E10'],
];
/** "Button / Modal Action" Save/Enabled - the register action. */
export const GOLD_BUTTON_FILL: Array<[string, string]> = [
	['0', '#FFF5B9'],
	['0.33', '#FFEE82'],
	['0.66', '#F5A300'],
	['1', '#CE7C00'],
];
/** Player rows and the countdown capsule. */
export const ROW_FILL: Array<[string, string]> = [
	['0', '#330B05'],
	['1', '#0F0402'],
];
/** "Tournament Entry Banner / Mobile". */
export const BANNER_FILL: Array<[string, string]> = [
	['0', '#571405'],
	['1', '#1F0503'],
];

export const orangeGlow = (radius: number, opacity = 1) =>
	({
		shadowColor: '#FF7200',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: opacity,
		shadowRadius: radius,
		elevation: Math.round(radius),
	}) as const;

export const redGlow = (radius: number, opacity = 1) =>
	({
		shadowColor: '#FF0000',
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: opacity,
		shadowRadius: radius,
	}) as const;

/** Whole chips with comma grouping, the way the design prints every amount ("1,250,000"). */
export const groupThousands = (value: number) =>
	String(Math.round(Number(value) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** The three prize places, first to third, from `prices` as an array or a {1,2,3} map. */
export const prizePlaces = (sng: Pick<SnG, 'prices'> | undefined): number[] => {
	const prices: any = sng?.prices;
	if (Array.isArray(prices)) return [0, 1, 2].map(i => Number(prices[i] ?? 0));
	return [1, 2, 3].map(place => Number(prices?.[place] ?? 0));
};

/** The backend has no display name for a sit & go, so it is named after its tier. */
export const tournamentTitle = (
	sng: Pick<SnG, 'entry_icon'> | undefined,
	t: (k: string, o?: any) => string,
) => {
	const tier = sng?.entry_icon ?? 'bronze';
	const fallback = `${tier.charAt(0).toUpperCase()}${tier.slice(1)} Sit & Go`;
	return t(`T_SITE_TOURNAMENT_NAME_${tier.toUpperCase()}`, {
		defaultValue: fallback,
	}).toUpperCase();
};

/** When a tournament closes: the start time if it has one, else its registration deadline. */
export const tournamentTarget = (source: any): number | null => {
	const value = source?.start_in ?? source?.close_at;
	if (!value) return null;
	const ms = new Date(value).getTime();
	return Number.isFinite(ms) ? ms : null;
};

export const roomIdOf = (sng: any): string | undefined => {
	const id = sng?.room_id ?? sng?.roomId ?? sng?.room ?? sng?.table ?? sng?.tableId;
	return id != null ? String(id) : undefined;
};
